import { z } from 'zod'

import { dateUtil, isNull } from '../../util'
import { parseCsvLengthToMm } from '../millsheet/validateAndNormalizeMillsheetCsvRecords'

export const deliveryNoteCsvColumnMapper = {
  constructionName: '現場名',
  certificateNo: '証明書番号',
  notificationNo: '通知番号',
  deliveryNoteNo: '納品書番号',
  orderSlipNo: '受注（伝票）番号',
  issuedDate: '発行日',
  manufacturerName: 'メーカー名',
  traderName: '商社名',
  deliveryDestination: '納入先名',
  totalWeight: '合計重量',
  buildingPart: '部位名',
  steelType: '鋼種',
  heatNumber: '製鋼番号',
  size: '寸法',
  length: '長さ',
  quantity: '員数',
  weight: '重量',
} as const

const optionalText = z.string().transform((value) => (value.trim() === '' ? null : value.trim()))
const integerPattern = /^(?:0|[1-9]\d*|[1-9]\d{0,2}(?:,\d{3})+)$/
const integer = z
  .string()
  .regex(integerPattern, '0以上の整数を正しい桁区切りで入力してください。')
  .transform((x) => Number(x.replace(/,/g, '')))

export type NormalizedDeliveryNoteCsvRecord = {
  constructionName: string
  certificateNo: string | null
  notificationNo: string | null
  deliveryNoteNo: string
  orderSlipNo: string | null
  issuedDate: Date
  manufacturerName: string
  traderName: string
  deliveryDestination: string
  totalWeight: number
  buildingPart: string | null
  steelType: string
  heatNumber: string
  size: string
  lengthMm: number
  quantity: number
  weight: number | null
  rank: number
}

export function validateAndNormalizeDeliveryNoteCsvRecord({
  record,
  index,
  lengthUnit,
}: {
  record: Record<string, string | number | null>
  index: number
  lengthUnit: 'm' | 'mm'
}) {
  const data = Object.fromEntries(
    Object.entries(deliveryNoteCsvColumnMapper).map(([key, label]) => [key, record[label]]),
  )
  const schema = z.object({
    constructionName: z.string().trim().min(1),
    certificateNo: optionalText,
    notificationNo: optionalText,
    deliveryNoteNo: z.string().trim().min(1),
    orderSlipNo: optionalText,
    issuedDate: z.string().trim().min(1),
    manufacturerName: z.string().trim().min(1),
    traderName: z.string().trim().min(1),
    deliveryDestination: z.string().trim().min(1),
    totalWeight: integer,
    buildingPart: optionalText,
    steelType: z.string().trim().min(1),
    heatNumber: z.string().trim().min(1),
    size: z.string().trim().min(1),
    length: z.string().trim().min(1),
    quantity: integer,
    weight: z.union([z.literal('').transform(() => null), integer]),
  })
  const parsed = schema.safeParse(data)
  if (!parsed.success) {
    return {
      success: false as const,
      index,
      record,
      message: parsed.error.issues
        .map((issue) => {
          const key = issue.path.join('') as keyof typeof deliveryNoteCsvColumnMapper
          return `${deliveryNoteCsvColumnMapper[key]}: ${issue.message}`
        })
        .join('。'),
    }
  }
  const issuedDate = dateUtil.parseDateFormatString(parsed.data.issuedDate)
  if (isNull(issuedDate)) {
    return { success: false as const, index, record, message: `発行日: YYYY/MM/DD形式で入力してください。` }
  }
  const length = parseCsvLengthToMm(parsed.data.length, lengthUnit)
  if (!length.success || length.lengthMm === null) {
    return {
      success: false as const,
      index,
      record,
      message: `長さ: ${length.success ? '必須です。' : length.message}`,
    }
  }
  return {
    success: true as const,
    index,
    record,
    resultTitle: parsed.data.deliveryNoteNo,
    result: {
      ...parsed.data,
      issuedDate: issuedDate.date.toDate(),
      lengthMm: length.lengthMm,
      rank: index + 1,
    } satisfies NormalizedDeliveryNoteCsvRecord,
  }
}
